/**
 * Project: academy-v2
 * File: EventItem
 * Author: wanpeninsula
 * Author URI: https://www.pennycodes.dev
 * Created: 25/01/2024 at 8:27 pm
 *
 * Copyright (c) 2024 academy-v2. All rights reserved.
 */
import GetEventIcon from "@/components/agenda/desktop/GetEventIcon.tsx";
import routes from "@/config/routes.ts";
import cn from "classnames";
import { FC } from "react";
import { Dayjs } from "dayjs";
import { getTitle, getTypeClass } from "@/utils/agenda.ts";
import { EventsItem, LoginUserItem } from "@/types";
import { useNavigate } from "react-router-dom";
import {
  MIXPANEL_PAGE_VERSION_MOBILE,
  maixpanelTrackEventProps,
} from "@/mixpanel/MixpanelUtils";
import { useMe } from "@/data/user";

type Props = {
  event: EventsItem;
  endTime: Dayjs;
  isPast: boolean;
  isPresent: boolean;
  isDeleted?: boolean;
  date: Dayjs;
  duration: number;
  startDate: Dayjs;
};

const EventItem: FC<Props> = (props) => {
  const {
    date,
    endTime,
    isPast,
    isPresent,
    isDeleted,
    event,
    startDate,
    duration,
  } = props;
  const { type } = event;
  const nomeVisualizzato = event.nome_visualizzato || "";
  const eventType = type ? type.toLowerCase() : "";
  const title =
    eventType === "correction" ? "Writing Correction" : getTitle(event);
  const navigate = useNavigate();
  const customStyle = getTypeClass(
    eventType,
    nomeVisualizzato,
    isPast,
    isDeleted,
  );

  // mixpanel and user info
  const { me } = useMe();
  const mixpanelPropsData = JSON.stringify(
    maixpanelTrackEventProps("Agenda", me?.item?.user as LoginUserItem),
  );

  return (
    <button
      data-track-props={mixpanelPropsData}
      data-track-eventtype={event.type}
      data-track-pageversion={MIXPANEL_PAGE_VERSION_MOBILE}
      onClick={() =>
        navigate(routes.agenda_event, {
          state: {
            isPast: isPast,
            title:
              event.nome_visualizzato == null || event.nome_visualizzato === ""
                ? event.titolo
                : event.nome_visualizzato,
            topic: event.titolo_argomento,
            date: startDate.toDate(),
            duration,
            descrizione: event.descrizione,
            tutor: event.tutors,
            link: isPast ? event.link_video : event.link_meet,
            id: event.id,
            materials: event.materials,
            endTime: endTime.toDate(),
            type: event.type,
            feedbackUser: event.feedback_user,
            canJoin: event.canJoin,
            nomeVisualizzato,
            isDeleted,
          },
        })
      }
      className={cn(
        "flex flex-row gap-x-2 items-center border   p-2 rounded-md",
        customStyle,
      )}
    >
      <GetEventIcon deleted={isDeleted} present={isPresent} past={isPast} />
      <div
        className={" flex flex-row gap-2 w-full items-center"}
        data-track-props={mixpanelPropsData}
        data-track-eventtype={event.type}
        data-track-pageversion={MIXPANEL_PAGE_VERSION_MOBILE}
      >
        <p
          data-track-props={mixpanelPropsData}
          data-track-eventtype={event.type}
          data-track-pageversion={MIXPANEL_PAGE_VERSION_MOBILE}
          className={"not-italic text-sm text-left font-normal line-clamp-1"}
        >
          {date.format("HH:mm")}&nbsp;-&nbsp;{endTime.format("HH:mm")}
        </p>
        <p
          data-track-props={mixpanelPropsData}
          data-track-eventtype={event.type}
          data-track-pageversion={MIXPANEL_PAGE_VERSION_MOBILE}
          translate="no"
          lang="en"
          className={
            "font-semibold overflow-hidden not-italic text-sm line-clamp-1 text-left"
          }
        >
          {title}
        </p>
      </div>
    </button>
  );
};

export default EventItem;
